import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useContextSelector } from "use-context-selector";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { toast } from "react-toastify";
import { FaCoins, FaHandshake } from "react-icons/fa";
import {
  FiAward, FiChevronLeft, FiChevronRight, FiClipboard, FiCopy,
  FiDollarSign, FiEye, FiEyeOff, FiFileText, FiMail, FiMessageSquare,
  FiRefreshCw, FiSettings, FiShield, FiTarget, FiTrendingUp, FiUserPlus,
} from "react-icons/fi";
import AuthContext from "../../context/AuthContext";
import "./MobileMemberDashboard.css";

const MENU_ICONS = {
  account: FiShield,
  betting: FiClipboard,
  accountRecord: FiFileText,
  profitLoss: FiTrendingUp,
  rewardCenter: FiAward,
  invite: FiUserPlus,
  mission: FiTarget,
  internal: FiMail,
  rebate: FiDollarSign,
};

const QUICK_ACTIONS = [
  { key: "deposit", label: "Deposit", image: "/assets/images/deposit.svg" },
  { key: "withdraw", label: "Withdraw", image: "/assets/images/withdraw.svg" },
  { key: "rewardCenter", label: "Reward Center", image: "/assets/images/vipicon.74604b3a.png" },
  { key: "invite", label: "Invite Friends", Icon: FaHandshake },
];

function VipArtwork() {
  return <svg viewBox="0 0 48 58" aria-hidden="true">
    <defs><linearGradient id="member-vip-gold" x2="0" y2="1"><stop stopColor="#fff5a4" /><stop offset="1" stopColor="#f1a61b" /></linearGradient></defs>
    <path d="m24 56-18-26 8-10h20l8 10-18 26Z" fill="url(#member-vip-gold)" stroke="#ffd260" strokeWidth="2" />
    <path d="m10 10 7 5 7-10 7 10 7-5-4 13H14l-4-13Z" fill="url(#member-vip-gold)" />
    <g fill="#ffe47c"><circle cx="10" cy="8" r="3" /><circle cx="24" cy="3" r="3" /><circle cx="38" cy="8" r="3" /></g>
    <path d="m16 30 8 14 8-14" stroke="#fffbd3" strokeWidth="5" strokeLinejoin="round" />
  </svg>;
}

function DownloadArtwork() {
  return <svg viewBox="0 0 56 56" aria-hidden="true">
    <path d="m7 31 21-9 21 9v16l-21 8-21-8V31Z" fill="#ffc34a" />
    <path d="m7 31 21 8 21-8-21-9-21 9Z" fill="#ffe47f" />
    <path d="M28 39v16l21-8V31l-21 8Z" fill="#e9a631" />
    <path d="M28 4v24m-9-9 9 9 9-9" stroke="#ffe47f" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}

export default function MobileMemberDashboard({ tab, onSelect, items, children }) {
  const navigate = useNavigate();
  const user = useContextSelector(AuthContext, ctx => ctx.user);
  const coins = useContextSelector(AuthContext, ctx => ctx.userCoins);
  const profile = useContextSelector(AuthContext, ctx => ctx.profileData);
  const refreshBalance = useContextSelector(AuthContext, ctx => ctx.amounutRefresh);
  const refreshing = useContextSelector(AuthContext, ctx => ctx.refreshAmountLoader);
  const [accountOpen, setAccountOpen] = useState(false);
  const [balanceVisible, setBalanceVisible] = useState(true);
  const username = user?.user?.username || user?.username || "—";
  const vipLevel = Math.max(0, (Number(profile?.userLevel) || 1) - 1);
  const dashboard = tab === "account" && !accountOpen;
  const openSection = key => {
    setAccountOpen(key === "account");
    onSelect(key);
  };
  const back = () => {
    if (dashboard) navigate("/");
    else {
      setAccountOpen(false);
      onSelect("account");
    }
  };
  const title = dashboard ? "Member" : items.find(([key]) => key === tab)?.[1] || ({ deposit: "Deposit", withdraw: "Withdrawal", vip: "VIP" })[tab] || "Member";

  return (
    <div className="chorki-mobile-member ref-member-embedded">
      <header className="chorki-mobile-member-header">
        <button type="button" onClick={back} aria-label={dashboard ? "Back to home" : "Back to member"}><FiChevronLeft aria-hidden="true" /></button>
        <h1>{title}</h1>
        <div>
          <button type="button" onClick={() => openSection("account")} aria-label="Account settings"><FiSettings aria-hidden="true" /></button>
          <button type="button" onClick={() => openSection("internal")} aria-label="Internal Message"><FiMessageSquare aria-hidden="true" /></button>
        </div>
      </header>

      {dashboard ? (
        <>
          <section className="chorki-mobile-member-profile" aria-label="Account summary">
            <button className="chorki-mobile-member-avatar" type="button" onClick={() => openSection("account")} aria-label="Open My Account"><img src="/assets/Icon/0.webp" alt="" /></button>
            <div className="chorki-mobile-member-identity">
              <div className="chorki-mobile-member-name">
                <strong>{username}</strong>
                <button type="button" className="chorki-mobile-member-vip" onClick={() => openSection("vip")}>VIP{vipLevel}</button>
                <CopyToClipboard text={username} onCopy={() => toast.success("Username copied")}>
                  <button type="button" className="chorki-mobile-member-copy" aria-label="Copy username"><FiCopy aria-hidden="true" /></button>
                </CopyToClipboard>
              </div>
              <div className="chorki-mobile-member-balance">
                <FaCoins className="chorki-mobile-member-coin" aria-hidden="true" />
                <span>{balanceVisible ? `৳ ${Number(coins?.balance || 0).toFixed(2)}` : "••••"}</span>
                <button type="button" onClick={refreshBalance} disabled={refreshing} aria-label="Refresh balance"><FiRefreshCw className={refreshing ? "refreshing" : ""} aria-hidden="true" /></button>
                <button type="button" onClick={() => setBalanceVisible(value => !value)} aria-label={balanceVisible ? "Hide balance" : "Show balance"}>{balanceVisible ? <FiEye aria-hidden="true" /> : <FiEyeOff aria-hidden="true" />}</button>
              </div>
            </div>
            <button className="chorki-mobile-member-profile-arrow" type="button" onClick={() => openSection("account")} aria-label="My Account"><FiChevronRight aria-hidden="true" /></button>
          </section>

          <section className="chorki-mobile-member-benefits" aria-label="VIP and app">
            <button type="button" onClick={() => openSection("vip")}><VipArtwork /><span><strong>VIP</strong><small>VIP PRIVILEGE</small></span></button>
            <Link to="/down"><DownloadArtwork /><span><strong>APP</strong><small>DOWNLOAD</small></span></Link>
          </section>

          <nav className="chorki-mobile-member-actions" aria-label="Wallet and rewards">
            {QUICK_ACTIONS.map(({ key, label, image, Icon }) => (
              <button key={key} type="button" onClick={() => openSection(key)}>
                <span className="chorki-mobile-member-action-icon">{image ? <img src={image} alt="" /> : <Icon aria-hidden="true" />}</span>
                <span>{label}</span>
              </button>
            ))}
          </nav>

          <nav className="chorki-mobile-member-menu" aria-label="Account sections">
            {items.map(([key, label, , , badge]) => {
              const Icon = MENU_ICONS[key];
              return (
                <button key={key} type="button" onClick={() => openSection(key)}>
                  <Icon className="chorki-mobile-member-menu-icon" aria-hidden="true" />
                  <span>{label}</span>
                  {badge ? <em>{badge}</em> : null}
                  <FiChevronRight className="chorki-mobile-member-menu-arrow" aria-hidden="true" />
                </button>
              );
            })}
          </nav>
        </>
      ) : (
        <div className="chorki-mobile-member-panel">{children}</div>
      )}
    </div>
  );
}
